import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { ArrowLeft, ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { LectureDownloader } from '@/components/LectureDownloader';

export function CardiovascularDiseaseLongevity() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && currentSlide > 0) {
        setCurrentSlide(prev => prev - 1);
      } else if (event.key === 'ArrowRight' && currentSlide < slides.length - 1) {
        setCurrentSlide(prev => prev + 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide]);

  const slides = [
    {
      title: "Cardiovascular Disease in the Age of Longevity: A Clinician's Guide to Health Optimization and Extended Healthspan",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h1 className="text-4xl font-light text-gray-900 mb-6">
            Cardiovascular Disease in the Age of Longevity
          </h1>
          <h2 className="text-2xl font-light text-blue-600 mb-8">
            A Clinician's Guide to Health Optimization and Extended Healthspan
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Bridging Traditional Cardiology with Proactive, Systems-Based Approaches
          </p>
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              Next Generation Medicine • Longevity Science
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Beyond Disease Management: The Cardiovascular Optimization Paradigm",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">The Challenge</h3>
            <p className="text-sm text-gray-700">
              Cardiovascular Disease (CVD) remains the #1 global cause of mortality
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Traditional Cardiology</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Focuses on managing established CVD</li>
                <li>• Reactive approach</li>
                <li>• Treats risk factors once clinically significant</li>
                <li>• "Repairing when broken"</li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-blue-800 mb-3">Longevity Medicine</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Proactive optimization before disease manifests</li>
                <li>• Enhances physiological reserve</li>
                <li>• Views CV system as capable of enhanced performance</li>
                <li>• "Cultivating robust cardiovascular performance"</li>
              </ul>
            </div>
          </div>

          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Paradigm Shift Visualization</h3>
            <svg width="100%" height="150" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 450 150">
              <defs>
                <marker id="arrow_blue" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#007bff" />
                </marker>
              </defs>
              <rect x="10" y="40" width="200" height="70" rx="5" ry="5" fill="#e0e0e0" stroke="#ccc"/>
              <text x="110" y="65" textAnchor="middle" fontFamily="Arial" fontSize="14" fill="#333">Traditional Approach</text>
              <text x="110" y="85" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#555">Disease Management</text>
              <text x="110" y="100" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#777">(Reactive, Threshold-based)</text>

              <line x1="220" y1="75" x2="250" y2="75" stroke="#007bff" strokeWidth="3" markerEnd="url(#arrow_blue)"/>

              <rect x="260" y="40" width="200" height="70" rx="5" ry="5" fill="#d1e7ff" stroke="#007bff"/>
              <text x="360" y="65" textAnchor="middle" fontFamily="Arial" fontSize="14" fill="#0056b3">Longevity Approach</text>
              <text x="360" y="85" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#0056b3">Health Optimization</text>
              <text x="360" y="100" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#004085">(Proactive, Optimal Function)</text>
            </svg>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Key Takeaway</h3>
            <p className="text-sm text-gray-700">
              Longevity medicine redefines cardiovascular health as achieving <strong>optimal function</strong> and resilience, not merely the absence of diagnosed disease.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Healthspan & Systems Biology: The \"Why\" and \"How\"",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Healthspan</h3>
            <p className="text-sm text-gray-700 mb-3">
              The duration of life spent in good health, free from chronic disease burden. CVD is a primary limiter of healthspan.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Goal:</strong> Optimizing cardiovascular health is fundamental to extending active, vital years.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">Systems-Biology Approach</h3>
            <div className="space-y-3">
              <div className="bg-white border border-gray-200 p-3 rounded">
                <h4 className="font-medium text-gray-800 mb-2">Interconnected Systems</h4>
                <p className="text-sm text-gray-700">
                  CVD is not an isolated organ issue; it's deeply interconnected with neuroendocrine, gastrointestinal, and immune systems.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 p-3 rounded">
                <h4 className="font-medium text-gray-800 mb-2">Root Cause Focus</h4>
                <p className="text-sm text-gray-700">
                  Identify and address chronic inflammation, oxidative stress, mitochondrial dysfunction, hormonal imbalances.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 p-3 rounded">
                <h4 className="font-medium text-gray-800 mb-2">Personalized Interventions</h4>
                <p className="text-sm text-gray-700">
                  Based on individual's unique biological makeup (genetics, epigenetics, lifestyle, environment).
                </p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg text-center">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Our Goal</h3>
            <p className="text-sm text-gray-700">
              Add <strong>life to years</strong>, not just years to life, by understanding the body as an integrated network.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Core Pillar 1: Chronic Inflammation & Oxidative Stress",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Foundational Drivers</h3>
            <p className="text-sm text-gray-700">
              Chronic low-grade inflammation and persistent oxidative stress are fundamental etiological factors in CVD development and progression.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Endothelial Dysfunction</h4>
                <p className="text-sm text-gray-700 mb-2">"The canary in the coal mine"</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Reduced Nitric Oxide (NO) bioavailability</li>
                  <li>• Increased vascular permeability</li>
                  <li>• Enhanced adhesion molecules (VCAM-1, ICAM-1)</li>
                  <li>• Active pathological process, not passive breakdown</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Pro-inflammatory Cytokines</h4>
                <p className="text-sm text-gray-700">
                  IL-1β, TNF-α, IL-6 directly contribute to vascular injury and perpetuate inflammation.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white border border-gray-200 p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">LDL Oxidation (oxLDL)</h4>
                <p className="text-sm text-gray-700">
                  Critical initiating event in atherosclerosis. oxLDL is highly atherogenic, leading to foam cell formation and plaque instability.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Clinical Application</h4>
                <div className="space-y-2">
                  <div>
                    <strong className="text-xs text-blue-700">Biomarkers:</strong>
                    <p className="text-xs text-gray-700">hsCRP, IL-6, TNF-α, MPO, Lp-PLA2, oxLDL, F2-Isoprostanes</p>
                  </div>
                  <div>
                    <strong className="text-xs text-blue-700">Interpretation:</strong>
                    <p className="text-xs text-gray-700">Elevated levels indicate ongoing vascular inflammation, oxidative damage</p>
                  </div>
                  <div>
                    <strong className="text-xs text-blue-700">Therapies:</strong>
                    <p className="text-xs text-gray-700">Anti-inflammatory diet, exercise, stress reduction, Curcumin, Omega-3s, NAC</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Pillar 2: The NO Axis - Master Regulator of Vascular Homeostasis",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Nitric Oxide (NO) Functions</h3>
            <p className="text-sm text-gray-700 mb-3">
              NO, primarily produced by endothelial NO synthase (eNOS), is crucial for:
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-blue-700 text-sm mb-1">Vasodilation</h4>
                <p className="text-xs text-gray-700">Regulating blood pressure and flow</p>
              </div>
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-blue-700 text-sm mb-1">Anti-atherogenic</h4>
                <p className="text-xs text-gray-700">Inhibiting platelet/monocyte adhesion, VSMC proliferation</p>
              </div>
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-blue-700 text-sm mb-1">Endothelial Repair</h4>
                <p className="text-xs text-gray-700">Anti-inflammatory signaling</p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">eNOS Uncoupling</h3>
            <p className="text-sm text-gray-700 mb-2">
              <strong>Critical pathology:</strong> eNOS produces superoxide (O2•−) instead of NO
            </p>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Often due to oxidative stress or cofactor (BH4) deficiency</li>
              <li>• Exacerbates vascular damage</li>
              <li>• Factors impairing NO: Low L-arginine/L-citrulline, BH4 deficiency, high ADMA</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">Clinical Application</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Biomarkers</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• ADMA/SDMA levels</li>
                  <li>• L-arginine/Citrulline ratio</li>
                  <li>• Flow-Mediated Dilation (FMD)</li>
                  <li>• EndoPAT</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Interpretation</h4>
                <p className="text-xs text-gray-700">
                  High ADMA, low L-Arg/Cit ratio, or impaired endothelial function suggest NO dysregulation
                </p>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Therapies</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• L-Citrulline: 3-6 g/day</li>
                  <li>• Nitrate-rich foods (leafy greens, beetroot)</li>
                  <li>• Exercise, antioxidants</li>
                  <li>• BH4: 5-10 mg/kg/day (Rx)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Pillar 3: Mitochondrial Integrity & Cellular Senescence",
      content: (
        <div className="space-y-6">
          <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-4">Energy & Quality Control</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 p-3 rounded">
                <h4 className="font-medium text-orange-700 mb-2">Mitochondrial Dysfunction</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Heart is energy-intensive organ</li>
                  <li>• Impaired ATP production</li>
                  <li>• Excessive ROS, mtDNA damage</li>
                  <li>• Central to cardiac aging and CVD</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 p-3 rounded">
                <h4 className="font-medium text-orange-700 mb-2">Autophagy/Mitophagy</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Essential cellular "housekeeping"</li>
                  <li>• Clears damaged organelles</li>
                  <li>• Declines with age</li>
                  <li>• Contributes to damage accumulation</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 p-3 rounded">
                <h4 className="font-medium text-orange-700 mb-2">Cellular Senescence</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Senescent cell accumulation</li>
                  <li>• SASP drives inflammation</li>
                  <li>• Cardiac hypertrophy, fibrosis</li>
                  <li>• Vascular stiffness</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Clinical Application</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Biomarkers</h4>
                <div className="space-y-3">
                  <div className="bg-white border border-gray-200 p-3 rounded">
                    <h5 className="font-medium text-blue-700 text-sm mb-1">Mitochondrial</h5>
                    <p className="text-xs text-gray-700">CoQ10 levels, Organic Acid Tests (OATs), Krebs cycle markers</p>
                  </div>
                  <div className="bg-white border border-gray-200 p-3 rounded">
                    <h5 className="font-medium text-blue-700 text-sm mb-1">Senescence</h5>
                    <p className="text-xs text-gray-700">SASP factors (IL-6, TNF-α, MMPs), p16INK4a (research)</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Therapies</h4>
                <div className="space-y-3">
                  <div className="bg-white border border-gray-200 p-3 rounded">
                    <h5 className="font-medium text-green-700 text-sm mb-1">Mitochondrial Support</h5>
                    <p className="text-xs text-gray-700">CoQ10 (100-300mg/day), PQQ (10-20mg), L-Carnitine, D-Ribose, SS-31</p>
                  </div>
                  <div className="bg-white border border-gray-200 p-3 rounded">
                    <h5 className="font-medium text-green-700 text-sm mb-1">Autophagy Enhancement</h5>
                    <p className="text-xs text-gray-700">Intermittent fasting, exercise, Rapamycin (low-dose, monitor)</p>
                  </div>
                  <div className="bg-white border border-gray-200 p-3 rounded">
                    <h5 className="font-medium text-green-700 text-sm mb-1">Senolytics</h5>
                    <p className="text-xs text-gray-700">Dasatinib + Quercetin, Fisetin (intermittent dosing, monitor)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Pillar 4: Modern Lipid & Fatty Acid Dynamics - Part 1",
      content: (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-4">Beyond Simple Cholesterol Numbers</h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-yellow-700 mb-3">Beyond LDL-C</h4>
                <p className="text-sm text-gray-700 mb-2">Traditional LDL-C can be misleading. Focus on:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>LDL Particle Number (LDL-P)</strong> or ApoB (better risk predictors)</li>
                  <li>• <strong>Small, dense LDL (sdLDL)</strong> particles (more atherogenic)</li>
                  <li>• <strong>Lipoprotein(a) [Lp(a)]</strong> (genetic, highly atherogenic)</li>
                  <li>• <strong>Oxidized LDL (oxLDL)</strong> (key driver of atherogenesis)</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-yellow-700 mb-3">Cholesterol Paradox</h4>
                <p className="text-sm text-gray-700 mb-2">
                  In some very elderly populations, higher Total or LDL Cholesterol is sometimes associated with lower mortality.
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Context matters:</strong> age, frailty, overall health status are crucial.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-4">Plasmalogens</h3>
            <p className="text-sm text-gray-700 mb-3">
              Unique phospholipids vital for cell membrane integrity, fluidity, and antioxidant defense.
            </p>
            <div className="bg-white border border-gray-200 p-3 rounded">
              <p className="text-sm text-gray-700">
                <strong>Clinical significance:</strong> Low circulating plasmalogen levels are independently associated with increased risk of all-cause mortality, CVD, and neurodegeneration.
              </p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">Clinical Application</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Biomarkers</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Advanced Lipid Panel (NMR LipoProfile)</li>
                  <li>• LDL-P, sdLDL, ApoB</li>
                  <li>• Lp(a), oxLDL</li>
                  <li>• Plasmalogen levels (ProdromeScan™)</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Interpretation</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• High LDL-P/ApoB/Lp(a)/oxLDL = ↑atherogenic risk</li>
                  <li>• Low plasmalogens = impaired membrane health</li>
                  <li>• ↑oxidative stress vulnerability</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Therapies</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Standard lipid-lowering where appropriate</li>
                  <li>• Guided by particle burden and Lp(a)</li>
                  <li>• Plasmalogen precursors (DHA-AAG)</li>
                  <li>• 900-3600 mg/day dosing</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Pillar 4: Modern Lipid & Fatty Acid Dynamics - Part 2",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Omega-3 Fatty Acids (EPA & DHA)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-blue-700 mb-2">Mechanisms</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Triglyceride lowering</li>
                  <li>• Anti-inflammatory (precursors to SPMs)</li>
                  <li>• Improve endothelial function</li>
                  <li>• May stabilize plaque (especially EPA)</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded">
                <h4 className="font-medium text-yellow-800 mb-2">REDUCE-IT Trial</h4>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Key finding:</strong> 4g/day icosapent ethyl (purified EPA) significantly reduced CV events in high-risk patients with elevated triglycerides despite statin therapy.
                </p>
                <p className="text-xs text-gray-600">
                  Mixed results in other trials highlight importance of dose, formulation, and patient selection.
                </p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 p-3 rounded mt-4">
              <p className="text-sm text-red-800">
                <strong>AHA 2023 CCD Guideline:</strong> Nonprescription fish oil/omega-3 supplements <em>not</em> recommended for patients with Chronic Coronary Disease for CV event reduction (highlights need for targeted, Rx use).
              </p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">Pentadecanoic Acid (C15:0, Fatty15™)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-green-700 mb-2">Overview</h4>
                <p className="text-sm text-gray-700 mb-2">
                  An odd-chain saturated fatty acid recently identified as an essential fatty acid.
                </p>
                <p className="text-sm text-gray-700">
                  Cell-based studies suggest C15:0 has broader and potentially safer cellular benefits compared to EPA alone.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-green-700 mb-2">Mechanisms</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Activates AMPK</li>
                  <li>• Inhibits mTOR</li>
                  <li>• Strengthens cell membranes</li>
                  <li>• Improves mitochondrial function</li>
                  <li>• Anti-inflammatory</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-4">Clinical Application</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-purple-700 text-sm mb-2">Biomarkers</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Omega-3 Index (target &gt;8%)</li>
                  <li>• C15:0 levels</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-purple-700 text-sm mb-2">Therapies</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• EPA: Prescription Icosapent Ethyl (Vascepa®) 2g BID</li>
                  <li>• High-quality Omega-3 supplements: 1-4 g/day</li>
                  <li>• C15:0 (Fatty15™): 100 mg/day</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-purple-700 text-sm mb-2">Potential Synergy</h4>
                <p className="text-xs text-gray-700">
                  C15:0 (membrane stability, mitochondrial health) + Omega-3s (SPM production, anti-inflammation)
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Pillar 5: Hormonal Influences",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Hormonal Harmony: Testosterone and E2/T Ratio</h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-blue-700 mb-3">Testosterone (in Men)</h4>
                <p className="text-sm text-gray-700 mb-2">
                  Low testosterone levels are an independent risk factor for atherosclerosis, metabolic syndrome, and increased cardiovascular and all-cause mortality.
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Benefits:</strong> Supports lean muscle mass, favorable lipid profiles, insulin sensitivity, and may have direct beneficial vascular effects.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-blue-700 mb-3">Estradiol:Testosterone (E2/T) Ratio</h4>
                <p className="text-sm text-gray-700 mb-2">
                  The balance between testosterone and estradiol (E2) is critical in both men and women.
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• In men, E2 is derived from testosterone via aromatase</li>
                  <li>• Higher T/E2 ratio associated with better epigenetic markers</li>
                  <li>• Excessive E2 suppression can be detrimental</li>
                  <li>• E2 itself has cardioprotective roles</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">Clinical Application (Men)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Biomarkers</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Total & Free Testosterone</li>
                  <li>• SHBG</li>
                  <li>• Estradiol (sensitive assay)</li>
                  <li>• LH, FSH</li>
                  <li>• Calculate E2/T ratio</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Interpretation</h4>
                <p className="text-xs text-gray-700">
                  Aim for physiological testosterone levels (mid-to-upper-normal range for age) and a balanced E2/T ratio. Avoid both low T and excessively high or low E2.
                </p>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Therapies</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• TRT for symptomatic hypogonadal men</li>
                  <li>• Individualized dosing, monitor hematocrit, PSA</li>
                  <li>• AIs (Anastrozole 0.25-0.5 mg 1-2x/week) if E2 elevated</li>
                  <li>• Weight management (key for aromatization)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Pillar 6: Specialized Pro-Resolving Mediators (SPMs)",
      content: (
        <div className="space-y-6">
          <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-4">Actively Terminating Inflammation</h3>
            <p className="text-sm text-gray-700 mb-4">
              The resolution of acute inflammation is not a passive decay but an <strong>active, highly orchestrated</strong> biological process.
            </p>
            
            <div className="bg-white border border-gray-200 p-4 rounded mb-4">
              <h4 className="font-medium text-purple-700 mb-2">What are SPMs?</h4>
              <p className="text-sm text-gray-700">
                SPMs (lipoxins, resolvins, protectins, maresins) are endogenous lipid mediators biosynthesized from omega-3 (EPA, DHA) and omega-6 (arachidonic acid for lipoxins) PUFAs.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-purple-700 mb-2">Mechanisms of Action</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Limit pro-inflammatory cytokine/chemokine production</li>
                  <li>• Inhibit further neutrophil infiltration</li>
                  <li>• Promote macrophage efferocytosis</li>
                  <li>• Stimulate tissue repair and regeneration</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-purple-700 mb-2">Clinical Significance</h4>
                <p className="text-sm text-gray-700 mb-2">
                  A "Resolution Deficit" (insufficient SPM production or impaired signaling) is implicated in the chronicity of atherosclerosis.
                </p>
                <p className="text-sm text-gray-700">
                  Animal models: SPM administration can reduce atheroprogression and promote plaque stability.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">Clinical Application</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Biomarkers</h4>
                <p className="text-xs text-gray-700">
                  SPM levels or ratios of SPMs to pro-inflammatory mediators (specialized labs, research-oriented currently)
                </p>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Interpretation</h4>
                <p className="text-xs text-gray-700">
                  Low SPMs or an unfavorable ratio may indicate impaired inflammation resolution capacity
                </p>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-green-700 text-sm mb-2">Therapies</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• High-dose Omega-3s (EPA/DHA) as precursors: 2-4 g/day</li>
                  <li>• Direct SPM supplementation (emerging field)</li>
                  <li>• "SPM Active" type products: 250-500 mcg total SPMs per serving</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Web of Interconnections: Gut, Stress, and the Heart",
      content: (
        <div className="space-y-6">
          <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-4">Gut-Heart Axis</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-orange-700 mb-2">Intestinal Permeability</h4>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>"Leaky Gut":</strong> Allows translocation of bacterial components and microbial metabolites into circulation.
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Lipopolysaccharide (LPS)</li>
                  <li>• Trimethylamine N-oxide (TMAO)</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-orange-700 mb-2">Cardiovascular Impact</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Drives systemic inflammation</li>
                  <li>• Contributes to endothelial dysfunction</li>
                  <li>• Linked to atherosclerosis</li>
                  <li>• Biomarkers: Zonulin, LPS, TMAO</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-4">Neuroendocrine Stress Response & Sleep</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-red-700 mb-2">Chronic HPA Axis Activation</h4>
                <p className="text-sm text-gray-700">
                  Sustained high cortisol promotes inflammation, insulin resistance, visceral adiposity.
                </p>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-red-700 mb-2">Sleep Disturbances (OSA)</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Induces hypoxia</li>
                  <li>• Sympathetic overactivation</li>
                  <li>• Oxidative stress, inflammation</li>
                  <li>• Impairs autophagic processes</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Clinical Application</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Testing</h4>
                <div className="space-y-3">
                  <div className="bg-white border border-gray-200 p-3 rounded">
                    <h5 className="font-medium text-blue-700 text-sm mb-1">Gut Health</h5>
                    <p className="text-xs text-gray-700">Comprehensive stool analysis, microbiome diversity, zonulin, serum LPS, TMAO</p>
                  </div>
                  <div className="bg-white border border-gray-200 p-3 rounded">
                    <h5 className="font-medium text-blue-700 text-sm mb-1">Stress/Sleep</h5>
                    <p className="text-xs text-gray-700">Salivary/urine cortisol profiles, HRV, Polysomnography for OSA</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Therapies</h4>
                <div className="space-y-3">
                  <div className="bg-white border border-gray-200 p-3 rounded">
                    <h5 className="font-medium text-green-700 text-sm mb-1">Gut Health</h5>
                    <p className="text-xs text-gray-700">Personalized diet, prebiotics/probiotics, L-glutamine, butyrate, address dysbiosis</p>
                  </div>
                  <div className="bg-white border border-gray-200 p-3 rounded">
                    <h5 className="font-medium text-green-700 text-sm mb-1">Stress/Sleep</h5>
                    <p className="text-xs text-gray-700">Stress management, adaptogens, sleep hygiene, CPAP for OSA</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Key Longevity Pathway 1: NAD⁺/Sirtuins & AMPK",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">NAD⁺/Sirtuin Pathway</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-blue-700 mb-2">Overview</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• NAD⁺ is a critical coenzyme</li>
                  <li>• Levels decline with age</li>
                  <li>• Sirtuins (SIRT1-SIRT7) are NAD⁺-dependent deacylases</li>
                  <li>• Declining NAD⁺ → Reduced Sirtuin activity → CV dysfunction</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-blue-700 mb-2">Key Sirtuins for CV Health</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li><strong>SIRT1:</strong> Promotes eNOS activity, reduces inflammation, protects against endothelial senescence</li>
                  <li><strong>SIRT3:</strong> Key mitochondrial deacetylase, maintains mitochondrial function, ATP production, reduces ROS</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">AMPK (AMP-Activated Protein Kinase)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-green-700 mb-2">The "Metabolic Master Switch"</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Cellular energy sensor</li>
                  <li>• Activated by low energy states</li>
                  <li>• Exercise, caloric restriction</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-green-700 mb-2">Effects</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Increases glucose uptake</li>
                  <li>• Fatty acid oxidation</li>
                  <li>• Mitochondrial biogenesis</li>
                  <li>• Promotes NO production</li>
                  <li>• Inhibits mTORC1</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-4">Clinical Application</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-purple-700 text-sm mb-2">Biomarkers</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• NAD⁺: Direct blood NAD⁺/NADH levels</li>
                  <li>• AMPK: Indirectly via metabolic markers</li>
                  <li>• Fasting glucose/insulin, lipids, HbA1c</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-purple-700 text-sm mb-2">NAD⁺/Sirtuin Support</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• NMN (250-900 mg/day)</li>
                  <li>• NR (250-1000 mg/day)</li>
                  <li>• Resveratrol (150-500+ mg/day)</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-purple-700 text-sm mb-2">AMPK Activators</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Metformin (500-2000 mg/day)</li>
                  <li>• Berberine (500 mg BID-TID)</li>
                  <li>• Exercise (HIIT, endurance)</li>
                  <li>• Caloric restriction/IF</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Key Longevity Pathway 2: mTOR & NF-κB",
      content: (
        <div className="space-y-6">
          <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-4">mTOR (Mechanistic Target of Rapamycin)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-orange-700 mb-2">Overview</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Central nutrient sensor</li>
                  <li>• Regulates cell growth, proliferation, metabolism</li>
                  <li>• mTOR Complex 1 (mTORC1) overactivity linked to cardiac aging</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-orange-700 mb-2">Pathological Effects</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Pathological hypertrophy</li>
                  <li>• Inhibition of autophagy</li>
                  <li>• Essential cellular cleanup impaired</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-4">NF-κB (Nuclear Factor kappa-B)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-red-700 mb-2">Master Inflammatory Regulator</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Transcriptional regulator of inflammation</li>
                  <li>• Controls immune responses</li>
                  <li>• Chronic activation = "inflammaging"</li>
                </ul>
              </div>
              
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-red-700 mb-2">Pathological Impact</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Drives pro-inflammatory cytokine expression</li>
                  <li>• Adhesion molecules</li>
                  <li>• Enzymes involved in atherosclerosis</li>
                  <li>• Heart failure progression</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Clinical Application</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-blue-700 text-sm mb-2">Biomarkers</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• mTOR: Metabolic markers, body composition</li>
                  <li>• Phospho-S6K (research)</li>
                  <li>• NF-κB: hsCRP, IL-6, TNF-α</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-blue-700 text-sm mb-2">mTOR Modulation</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Caloric restriction</li>
                  <li>• Intermittent fasting</li>
                  <li>• Rapamycin (low-dose, e.g., 5-6 mg/week)</li>
                  <li>• Off-label, requires monitoring</li>
                  <li>• PEARL trial showed safety benefits</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-blue-700 text-sm mb-2">NF-κB Inhibition</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Anti-inflammatory diet</li>
                  <li>• Curcumin (500-1000 mg/day)</li>
                  <li>• Omega-3 fatty acids</li>
                  <li>• Resveratrol, NAC</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Application: Comprehensive Assessment Strategy",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Beyond Standard Risk Factors</h3>
            <p className="text-sm text-gray-700">
              Move beyond standard risk factors to a multi-system, proactive evaluation focused on advanced biomarkers, imaging, and functional assessments.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                <h4 className="font-medium text-purple-800 mb-3">Advanced Imaging</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li><strong>Coronary Artery Calcium (CAC) Score:</strong> Quantifies calcified plaque</li>
                  <li><strong>Carotid Intima-Media Thickness (CIMT):</strong> Assesses arterial wall thickness, early atherosclerosis</li>
                  <li><strong>CT Coronary Angiography (CTCA):</strong> Visualizes coronary arteries, non-calcified plaque</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <h4 className="font-medium text-green-800 mb-3">Functional Assessments</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li><strong>Cardiopulmonary Exercise Testing (CPET):</strong> Gold standard for VO2 max (key longevity marker)</li>
                  <li><strong>Endothelial Function Testing:</strong> EndoPAT, FMD</li>
                  <li><strong>Heart Rate Variability (HRV):</strong> Assesses autonomic balance</li>
                </ul>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-4">Biomarker Panel for Longevity CVD Assessment</h4>
              <svg width="100%" height="280" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 280">
                <style>
                  {`.header { font-family: Arial, sans-serif; font-size: 13px; font-weight: bold; fill: #2c3e50; }
                    .cell { font-family: Arial, sans-serif; font-size: 12px; fill: #34495e; }
                    .category { font-family: Arial, sans-serif; font-size: 12px; font-weight: bold; fill: #2980b9; }`}
                </style>
                <rect x="10" y="10" width="580" height="30" fill="#ecf0f1" stroke="#bdc3c7"/>
                <text x="25" y="30" className="header">Category</text>
                <text x="180" y="30" className="header">Key Biomarkers</text>
                <text x="430" y="30" className="header">Clinical Relevance</text>

                <rect x="10" y="40" width="580" height="35" fill="#ffffff" stroke="#bdc3c7"/>
                <text x="25" y="63" className="category">Inflammation/Ox. Stress</text>
                <text x="180" y="63" className="cell">hsCRP, oxLDL, MPO, Lp-PLA2, F2-IsoP, IL-6</text>
                <text x="430" y="63" className="cell">Vascular damage, plaque risk</text>

                <rect x="10" y="75" width="580" height="35" fill="#f8f9f9" stroke="#bdc3c7"/>
                <text x="25" y="98" className="category">Endothelial Function</text>
                <text x="180" y="98" className="cell">ADMA/SDMA, (EndoPAT/FMD results)</text>
                <text x="430" y="98" className="cell">NO bioavailability, vasodilation</text>

                <rect x="10" y="110" width="580" height="35" fill="#ffffff" stroke="#bdc3c7"/>
                <text x="25" y="133" className="category">Advanced Lipids</text>
                <text x="180" y="133" className="cell">LDL-P, ApoB, Lp(a), sdLDL, Omega-3 Idx, C15:0</text>
                <text x="430" y="133" className="cell">Atherogenic burden, membrane health</text>

                <rect x="10" y="145" width="580" height="35" fill="#f8f9f9" stroke="#bdc3c7"/>
                <text x="25" y="168" className="category">Metabolic Health</text>
                <text x="180" y="168" className="cell">F. Insulin, HOMA-IR, HbA1c, Adiponectin, Leptin</text>
                <text x="430" y="168" className="cell">Insulin resistance, metabolic flexibility</text>

                <rect x="10" y="180" width="580" height="35" fill="#ffffff" stroke="#bdc3c7"/>
                <text x="25" y="203" className="category">Mito/Senescence/NAD+</text>
                <text x="180" y="203" className="cell">CoQ10, OATs, Blood NAD+, (SASP markers)</text>
                <text x="430" y="203" className="cell">Energy, cell aging, repair capacity</text>

                <rect x="10" y="215" width="580" height="35" fill="#f8f9f9" stroke="#bdc3c7"/>
                <text x="25" y="238" className="category">Hormonal Balance</text>
                <text x="180" y="238" className="cell">T (Total/Free), E2 (sens.), SHBG, DHEA-S, Cortisol</text>
                <text x="430" y="238" className="cell">Anabolic support, systemic effects</text>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Personalized Interventions & Dynamic Monitoring",
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">Foundation (Universal Recommendations)</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white border border-gray-200 p-3 rounded text-center">
                <h4 className="font-medium text-green-700 text-sm mb-2">Diet</h4>
                <p className="text-xs text-gray-700">Personalized (Mediterranean, low-glycemic, plant-rich, time-restricted eating)</p>
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded text-center">
                <h4 className="font-medium text-green-700 text-sm mb-2">Exercise</h4>
                <p className="text-xs text-gray-700">Zone 2 cardio, HIIT, strength training</p>
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded text-center">
                <h4 className="font-medium text-green-700 text-sm mb-2">Sleep</h4>
                <p className="text-xs text-gray-700">Optimize duration and quality</p>
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded text-center">
                <h4 className="font-medium text-green-700 text-sm mb-2">Stress</h4>
                <p className="text-xs text-gray-700">Mindfulness, meditation, yoga</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Targeted Supplementation/Therapeutics</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-blue-700 text-sm mb-1">Nutrient Repletion</h4>
                  <p className="text-xs text-gray-700">Vitamin D, Magnesium, B-vitamins</p>
                </div>
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-blue-700 text-sm mb-1">Anti-inflammatories/Antioxidants</h4>
                  <p className="text-xs text-gray-700">Curcumin, Omega-3s (EPA/DHA), NAC, Resveratrol, SPMs</p>
                </div>
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-blue-700 text-sm mb-1">Mitochondrial Support</h4>
                  <p className="text-xs text-gray-700">CoQ10, PQQ, L-Carnitine</p>
                </div>
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-blue-700 text-sm mb-1">NO Support</h4>
                  <p className="text-xs text-gray-700">L-Citrulline, beetroot extract, BH4 (if indicated)</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-blue-700 text-sm mb-1">Lipid Modulators</h4>
                  <p className="text-xs text-gray-700">Niacin, Bergamot, C15:0, prescription EPA</p>
                </div>
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-blue-700 text-sm mb-1">Hormone Optimization</h4>
                  <p className="text-xs text-gray-700">TRT, judicious AI use if needed</p>
                </div>
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-blue-700 text-sm mb-1">Longevity Pathway Modulators</h4>
                  <p className="text-xs text-gray-700">Metformin, Berberine, NMN/NR, low-dose Rapamycin</p>
                </div>
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <h4 className="font-medium text-blue-700 text-sm mb-1">Peptides (Emerging)</h4>
                  <p className="text-xs text-gray-700">BPC-157, CJC-1295/Ipamorelin, Thymosin Alpha-1</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Monitoring & Follow-up: "Test-Assess-Address-Retest" Cycle</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• Regular reassessment of key biomarkers (every 3-6 months initially, then 6-12 months)</li>
              <li>• Track functional improvements (VO2 max, HRV), symptoms, and quality of life</li>
              <li>• Dynamically adjust interventions based on response and evolving data</li>
              <li>• This is an N-of-1, iterative approach</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Evidence Snapshot: Key Studies & Findings",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">REDUCE-IT Trial (Icosapent Ethyl for Hypertriglyceridemia)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-blue-700 mb-2">Key Finding</h4>
                <p className="text-sm text-gray-700">
                  4g/day Icosapent Ethyl (pure EPA) significantly reduced major adverse CV events (25% RRR) in statin-treated patients with high TGs (135-499 mg/dL) and established CVD or Diabetes + risk factors.
                </p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded">
                <h4 className="font-medium text-yellow-800 mb-2">Details/Critique</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Benefit primarily in those with baseline TGs &gt;150 mg/dL</li>
                  <li>• Mechanism likely extends beyond TG lowering</li>
                  <li>• Anti-inflammatory, plaque stabilization</li>
                  <li>• Highlights specificity of EPA at high doses</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-4">NMN Human Trials (Liao et al., 2021; Igarashi et al., 2022)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-green-700 mb-2">Key Finding</h4>
                <p className="text-sm text-gray-700">
                  NMN supplementation (typically 250-900 mg/day) is generally safe and effective in increasing blood NAD⁺ levels in middle-aged and older adults. Some studies show improvements in insulin sensitivity and physical performance.
                </p>
              </div>
              
              <div className="bg-orange-50 border border-orange-200 p-4 rounded">
                <h4 className="font-medium text-orange-800 mb-2">Details/Critique</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Most trials are short-term with small sample sizes</li>
                  <li>• Focus on NAD+ boosting and surrogate markers</li>
                  <li>• Long-term cardiovascular outcome data needed</li>
                  <li>• FDA changed NMN's supplement status</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-4">PEARL Trial (Low-Dose Rapamycin for Healthspan)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 p-4 rounded">
                <h4 className="font-medium text-purple-700 mb-2">Key Finding</h4>
                <p className="text-sm text-gray-700">
                  Intermittent low-dose rapamycin (5-6 mg once weekly) in healthy older adults (mean age ~58) over 1 year was generally well-tolerated. Showed modest benefits in lean mass (women), self-reported pain reduction, and emotional well-being.
                </p>
              </div>
              
              <div className="bg-red-50 border border-red-200 p-4 rounded">
                <h4 className="font-medium text-red-800 mb-2">Details/Critique</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Exploratory trial focused on healthspan metrics</li>
                  <li>• Not powered for CV events or disease treatment</li>
                  <li>• Long-term effects require further investigation</li>
                  <li>• Inter-individual variability noted</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Conclusion: Charting a Course for Cardiovascular Longevity",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg text-center">
            <h3 className="text-2xl font-light text-blue-800 mb-4">Cardiovascular Longevity</h3>
            <p className="text-lg text-gray-700 font-light">
              A Transformative, Proactive, Systems-Based Paradigm
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-4">Paradigm Shift</h3>
              <p className="text-sm text-gray-700 mb-4">
                The focus shifts from mere disease management to the optimization of cardiovascular function and resilience throughout an extended lifespan.
              </p>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <h4 className="font-medium text-green-700 mb-2">Ultimate Goal</h4>
                <p className="text-sm text-gray-700">
                  To extend <strong>healthspan</strong> by preserving cardiovascular vigor, enabling individuals to lead longer, healthier, and more active lives.
                </p>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-purple-800 mb-4">Key Pillars to Address</h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Chronic Inflammation & Oxidative Stress</li>
                <li>• Nitric Oxide Axis</li>
                <li>• Mitochondrial Health & Senescence</li>
                <li>• Modern Lipid & Fatty Acid Dynamics</li>
                <li>• Hormonal Balance</li>
                <li>• Inflammation Resolution (SPMs)</li>
              </ul>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-4">Leveraging Key Longevity Pathways</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white p-3 rounded border text-center">
                <h4 className="font-medium text-orange-700 text-sm">NAD⁺/Sirtuins</h4>
              </div>
              <div className="bg-white p-3 rounded border text-center">
                <h4 className="font-medium text-orange-700 text-sm">AMPK</h4>
              </div>
              <div className="bg-white p-3 rounded border text-center">
                <h4 className="font-medium text-orange-700 text-sm">mTOR</h4>
              </div>
              <div className="bg-white p-3 rounded border text-center">
                <h4 className="font-medium text-orange-700 text-sm">NF-κB</h4>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-4">Clinical Application is Key</h3>
            <p className="text-sm text-gray-700 mb-3">
              This involves comprehensive, advanced assessments, personalized multi-modal interventions (lifestyle, targeted nutraceuticals, peptides, pharmaceuticals), and dynamic, data-driven monitoring.
            </p>
            <div className="bg-blue-900 text-white p-4 rounded text-center">
              <h4 className="font-medium text-blue-100 mb-2">Call to Action</h4>
              <p className="text-sm text-blue-100">
                Embrace this evolving paradigm to empower your patients in their journey towards optimal cardiovascular health and longevity.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Questions & Discussion",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <svg className="w-24 h-24 text-blue-600 mb-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="text-4xl font-light text-gray-900 mb-6">
            Questions & Discussion
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Let's explore how these cardiovascular longevity principles can be integrated into your clinical practice
          </p>
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg max-w-2xl">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Discussion Topics</h3>
            <ul className="text-sm text-gray-700 space-y-2 text-left">
              <li>• Implementation strategies for comprehensive assessments</li>
              <li>• Patient selection criteria for advanced interventions</li>
              <li>• Integration with existing cardiology protocols</li>
              <li>• Monitoring and follow-up considerations</li>
              <li>• Risk-benefit analysis for emerging therapies</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Selected References",
      content: (
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Primary References</h3>
            <div className="space-y-3 text-xs text-gray-700">
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>1.</strong> Katcher, H. I., et al. (2024). Climbing the longevity pyramid: overview of evidence-driven healthcare prevention strategies for human longevity. <em>Front. Aging</em>, 5, 1495029.
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>2.</strong> Libby, P. (2021). The Role of Inflammation in Cardiovascular Disease. <em>J Am Coll Cardiol</em>, 78(8), 820-830.
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>3.</strong> Förstermann, U., & Sessa, W. C. (2012). Nitric oxide synthases: regulation and function. <em>Eur Heart J</em>, 33(7), 829–837.
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>4.</strong> Covarrubias, A. J., et al. (2021). NAD+ metabolism and its roles in cellular processes during ageing. <em>Nat Rev Mol Cell Biol</em>, 22(2), 119-141.
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>5.</strong> Saxton, R. A., & Sabatini, D. M. (2017). mTOR Signaling in Growth, Metabolism, and Disease. <em>Cell</em>, 168(6), 960-976.
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Additional References</h3>
            <div className="space-y-3 text-xs text-gray-700">
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>6.</strong> Salminen, A., & Kaarniranta, K. (2012). AMP-activated protein kinase (AMPK) controls the aging process via an integrated signaling network. <em>Ageing Res Rev</em>, 11(2), 230-241.
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>7.</strong> Bhatt, D.L., et al. (REDUCE-IT Investigators). (2019). Cardiovascular Risk Reduction with Icosapent Ethyl for Hypertriglyceridemia. <em>N Engl J Med</em>, 380(1), 11-22.
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>8.</strong> Kaeberlein, M., et al. (PEARL Consortium). (2024). Influence of rapamycin on safety and healthspan metrics after one year: PEARL trial results. <em>Aging (Albany NY)</em>, 16.
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>9.</strong> Venners, S.A., et al. (2023). Pentadecanoic Acid (C15:0), an Essential Fatty Acid, Shares Clinically Relevant Cell-Based Activities with Leading Longevity-Enhancing Compounds. <em>Nutrients</em>, 15(22), 4859.
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded">
                <strong>10.</strong> Serhan, C. N. (2017). Treating inflammation and infection in the 21st century: new hints from decoding resolution mediators and mechanisms. <em>FASEB J</em>, 31(4), 1273-1288.
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "SHIFT Meta-Framework: Full-Spectrum Clinician's Cheat-Sheet",
      content: (
        <div className="space-y-4 overflow-y-auto max-h-full">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-2">Cardiovascular Disease in the Age of Longevity</h3>
            <p className="text-sm text-gray-700">
              SHIFT Meta-Framework: Full-Spectrum Clinician's Cheat-Sheet
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-2 text-left min-w-24">SHIFT Domain</th>
                  <th className="border border-gray-300 p-2 text-left min-w-32">Key CV-Aging Pressures & Pathways</th>
                  <th className="border border-gray-300 p-2 text-left min-w-32">Key Biomarkers & Diagnostics</th>
                  <th className="border border-gray-300 p-2 text-left min-w-32">Targeted Interventions</th>
                  <th className="border border-gray-300 p-2 text-left min-w-32">Monitoring & Follow-Up</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-red-50">
                  <td className="border border-gray-300 p-2 font-medium">S – Sensing & Metabolic fuel-gauging</td>
                  <td className="border border-gray-300 p-2">• ↓AMPK / ↑mTOR<br/>• Brain-insulin-resistance (IIS)<br/>• NAD⁺ decline / Sirtuin drift<br/>• Mitochondrial dyshomeostasis</td>
                  <td className="border border-gray-300 p-2">• Fasting insulin, HOMA-IR monthly<br/>• NAD⁺ RBC level q6 mo<br/>• CoQ10, OATs for mitochondrial function</td>
                  <td className="border border-gray-300 p-2">• NMN / NR (+ resveratrol)<br/>• C15:0 (AMPK↑/mTOR↓)<br/>• Metformin, Berberine<br/>• CoQ10, PQQ, L-Carnitine</td>
                  <td className="border border-gray-300 p-2">• Metabolic markers q3 mo<br/>• VO2 max testing annually<br/>• Body composition analysis</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="border border-gray-300 p-2 font-medium">H – Homeostatic Defence, Stress-Repair</td>
                  <td className="border border-gray-300 p-2">• Autophagy insufficiency<br/>• Impaired BDNF/NGF<br/>• Senescent cells (p16INK4a)<br/>• Oxidative stress overload</td>
                  <td className="border border-gray-300 p-2">• F2-Isoprostanes, 8-OHdG<br/>• SASP markers (IL-6, SASP)<br/>• HRV, cortisol rhythm</td>
                  <td className="border border-gray-300 p-2">• Intermittent fasting<br/>• Low-dose Rapamycin (monitor)<br/>• NAC, Vitamin C/E<br/>• Senolytics (Dasatinib + Quercetin)</td>
                  <td className="border border-gray-300 p-2">• Oxidative stress markers q6 mo<br/>• Sleep staging monthly<br/>• Stress management compliance</td>
                </tr>
                <tr className="bg-orange-50">
                  <td className="border border-gray-300 p-2 font-medium">I – Inflammation & Immune Tone</td>
                  <td className="border border-gray-300 p-2">• NF-κB / JAK-STAT activation<br/>• Cytokine storm: IL-6, TNF-α, IL-1β<br/>• Resolution deficit – low SPMs<br/>• Gut-heart axis (LPS, TMAO)</td>
                  <td className="border border-gray-300 p-2">• hs-CRP, IL-6, TNF-α, MPO<br/>• LPS, zonulin, TMAO<br/>• SPM panel (research)<br/>• Microbiome diversity</td>
                  <td className="border border-gray-300 p-2">• High-dose EPA/DHA (2-4 g/day)<br/>• Direct SPM supplements<br/>• Curcumin, probiotics<br/>• Anti-inflammatory diet</td>
                  <td className="border border-gray-300 p-2">• hs-CRP/IL-6 every 3 mo<br/>• Gut health markers q6 mo<br/>• Track dietary compliance</td>
                </tr>
                <tr className="bg-purple-50">
                  <td className="border border-gray-300 p-2 font-medium">F – Fibrosis / Structural Remodeling</td>
                  <td className="border border-gray-300 p-2">• TGF-β/Smad axis overactivation<br/>• eNOS uncoupling, low NO<br/>• Collagen cross-linking, arterial stiffening<br/>• Membrane fragility (low plasmalogens)</td>
                  <td className="border border-gray-300 p-2">• NT-proBNP, Galectin-3, sST2<br/>• ADMA/SDMA, FMD, EndoPAT<br/>• CAC score, CIMT, PWV<br/>• Plasmalogen levels</td>
                  <td className="border border-gray-300 p-2">• L-Citrulline (3-6 g/day)<br/>• BH4 supplementation<br/>• Plasmalogen precursors (DHA-AAG)<br/>• ACE inhibitors/ARBs</td>
                  <td className="border border-gray-300 p-2">• Cardiac biomarkers q3 mo<br/>• Echo strain analysis yearly<br/>• Vascular function testing</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="border border-gray-300 p-2 font-medium">T – Time-Keeping & Chronobiology</td>
                  <td className="border border-gray-300 p-2">• Circadian clock disruption<br/>• Cortisol-melatonin rhythm dysregulation<br/>• Sleep fragmentation, OSA<br/>• Feeding window misalignment</td>
                  <td className="border border-gray-300 p-2">• 4-point salivary cortisol<br/>• Sleep staging, HRV patterns<br/>• OSA screening (STOP-BANG, PSG)<br/>• Chronotype assessment</td>
                  <td className="border border-gray-300 p-2">• Consistent sleep window (22:30-06:30)<br/>• Morning light exposure (≥10k lux)<br/>• Time-restricted feeding<br/>• CPAP/MAD for OSA</td>
                  <td className="border border-gray-300 p-2">• HRV trends weekly<br/>• Sleep quality metrics<br/>• OSA compliance monitoring<br/>• Circadian rhythm assessment</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">How to Read/Use</h3>
            <ol className="text-sm text-gray-700 space-y-2">
              <li>1. Start with S-domain to map your patient's metabolic "dashboard"; correct insulin-NAD-NO triad first.</li>
              <li>2. Layer in H- and I-domains to restore defense & extinguish inflammation (check CRP trajectory).</li>
              <li>3. Address F-domain when biomarkers flag early remodeling; combine RAAS blockade + membrane support.</li>
              <li>4. Synchronize circadian T tactics—sleep, feeding window—to lock in gains.</li>
              <li>5. Iterate every 3–6 months: TEST → ASSESS → ADDRESS → RE-TEST.</li>
            </ol>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            <Link href="/">
              <Home className="w-4 h-4 text-gray-500 hover:text-gray-700" />
            </Link>
            <span className="text-gray-400">/</span>
            <Link href="/mentorship" className="text-gray-600 hover:text-gray-800">
              Mentorship Program
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Cardiovascular Disease in the Age of Longevity</span>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link href="/mentorship" className="inline-flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Mentorship Program
            </Link>
            <LectureDownloader lectureName="Cardiovascular Disease Longevity" fileName="CardiovascularDiseaseLongevity" />
          </div>
          <h1 className="text-3xl font-light text-gray-900 mb-2">Cardiovascular Disease in the Age of Longevity</h1>
          <p className="text-gray-600 font-light">
            A Clinician's Guide to Health Optimization and Extended Healthspan
          </p>
        </div>

        {/* Slideshow Container */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Slide Content */}
          <div className="relative">
            <div className="h-[500px] p-8 bg-gradient-to-br from-gray-50 to-white">
              <div className="h-full flex flex-col">
                <h2 className="text-2xl font-light text-gray-900 mb-6 text-center">
                  {slides[currentSlide].title}
                </h2>
                <div className="flex-1 overflow-y-auto">
                  {slides[currentSlide].content}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-light"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 font-light">
                {currentSlide + 1} of {slides.length}
              </span>
            </div>

            {/* Slide Indicators */}
            <div className="flex space-x-1">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full ${
                    index === currentSlide ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-light"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Embedded Audio Lecture */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Accompanying Audio Lecture</h3>
            <p className="text-sm text-gray-600 mt-1">
              Listen to the comprehensive audio guide while navigating through the slides
            </p>
          </div>
          <div className="p-6 flex justify-center">
            <iframe 
              src="https://share.descript.com/embed/5mFb50xTknI" 
              width="640" 
              height="360" 
              frameBorder="0" 
              allowFullScreen
              className="rounded-lg shadow-sm"
              title="Cardiovascular Disease Longevity Audio Lecture"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Keyboard Navigation */}
      <div className="fixed bottom-4 right-4 bg-white p-2 rounded-lg shadow-md">
        <p className="text-xs text-gray-500 font-light">Use ← → arrows to navigate</p>
      </div>
    </div>
  );
}